/**
 * Acceso y cambio de contraseña.
 *
 * El superadmin crea las cuentas y entrega las contraseñas en mano: no hay
 * registro ni recuperación por correo. El primer acceso obliga a cambiarla, y
 * el API bloquea el resto de las rutas hasta que ocurra.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, ApiError, NetworkError } from '../api/client';
import { useApp } from '../store';

export function LoginScreen() {
  const { login } = useApp();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await login(identifier.trim(), password);
    } catch (e) {
      if (e instanceof NetworkError) {
        setError(
          'Sin conexión. El primer acceso requiere red; después la aplicación funciona sin señal.',
        );
      } else if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError('No se pudo iniciar sesión.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="center-screen">
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 400 }}>
        <div className="center" style={{ marginBottom: 28 }}>
          <h1 className="screen-title">Sondeo Tecate 2027</h1>
          <p className="screen-sub">Levantamiento de campo · Calimetría</p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="identifier">
            Código de operador
          </label>
          <input
            id="identifier"
            className="input"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoCapitalize="characters"
            autoComplete="username"
            autoCorrect="off"
            spellCheck={false}
            required
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="password">
            Contraseña
          </label>
          <input
            id="password"
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        {error ? <div className="notice notice--danger">{error}</div> : null}

        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>

        <p className="faint center" style={{ marginTop: 20 }}>
          Si no tienes credenciales, solicítalas a tu coordinador. No hay registro propio.
        </p>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

export function ChangePasswordScreen() {
  const { markPasswordChanged, operator, logout, pending } = useApp();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bloqueado, setBloqueado] = useState<number | null>(null);

  /**
   * Salida de esta pantalla sin cambiar la contraseña.
   *
   * Hasta ahora no había ninguna: `App.tsx` devuelve esta pantalla y nada más
   * mientras `mustChangePassword` siga en pie, así que quien entraba con la
   * cuenta equivocada —o recibía un teléfono con la sesión de otro— se quedaba
   * encerrado, y la única salida era desinstalar la aplicación o borrar los
   * datos del navegador, que se lleva por delante las entrevistas sin
   * sincronizar.
   *
   * Cerrar sesión aquí respeta la misma protección que en Ajustes: si hay
   * trabajo sin enviar no se borra el dispositivo a la primera.
   */
  const cerrarSesion = async (force: boolean) => {
    setBusy(true);
    try {
      const result = await logout(force);
      if (!result.ok) setBloqueado(result.pending ?? 0);
    } finally {
      setBusy(false);
    }
  };

  const sinEnviar = pending.interviews + pending.households + pending.rejected;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (next !== confirm) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.changePassword(current, next);
      markPasswordChanged();
      navigate('/', { replace: true });
    } catch (e) {
      if (e instanceof NetworkError) {
        setError('Sin conexión. El cambio de contraseña requiere red.');
      } else if (e instanceof ApiError) {
        const fieldMessages = e.fieldErrors
          ? Object.values(e.fieldErrors).flat().join(' ')
          : null;
        setError(fieldMessages ?? e.message);
      } else {
        setError('No se pudo cambiar la contraseña.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="center-screen">
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 400 }}>
        <h1 className="screen-title">Cambia tu contraseña</h1>
        <p className="screen-sub">
          {operator?.name}, la contraseña que te entregó el administrador es temporal. Elige una
          propia para continuar.
        </p>

        <div className="field">
          <label className="field__label" htmlFor="current">
            Contraseña actual
          </label>
          <input
            id="current"
            className="input"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="next">
            Contraseña nueva
          </label>
          <input
            id="next"
            className="input"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            required
            minLength={10}
          />
          <p className="faint" style={{ marginTop: 6 }}>
            Al menos 10 caracteres, con una letra y un número.
          </p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="confirm">
            Repite la contraseña nueva
          </label>
          <input
            id="confirm"
            className="input"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        {error ? <div className="notice notice--danger">{error}</div> : null}

        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {busy ? 'Guardando…' : 'Guardar y continuar'}
        </button>

        {/* ---------------------- Salida de la pantalla -------------------- */}

        <div style={{ marginTop: 28, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          {bloqueado !== null ? (
            <div className="notice notice--warning">
              <strong>
                Hay {bloqueado} registro{bloqueado === 1 ? '' : 's'} sin enviar en este equipo.
              </strong>{' '}
              Cerrar sesión borra los datos del dispositivo y ese trabajo se perdería. Conéctate y
              sincroniza antes, o confirma que quieres descartarlo.
              <button
                type="button"
                className="btn btn--danger btn--block"
                style={{ marginTop: 12 }}
                disabled={busy}
                onClick={() => void cerrarSesion(true)}
              >
                Cerrar sesión y descartar lo no enviado
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--block"
                style={{ marginTop: 6 }}
                onClick={() => setBloqueado(null)}
              >
                Mejor no
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="btn btn--secondary btn--block"
                disabled={busy}
                onClick={() => void cerrarSesion(false)}
              >
                {busy ? 'Cerrando…' : 'Cerrar sesión'}
              </button>
              <p className="faint center" style={{ marginTop: 8 }}>
                {sinEnviar > 0
                  ? `Este equipo tiene ${sinEnviar} registro(s) sin enviar.`
                  : '¿No es tu cuenta? Sal y entra con la tuya.'}
              </p>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
